import React, { useEffect, useState, FormEvent } from 'react';
import { useTournament, TournamentSummary, completeTournamentCreation } from '@shootout-comps/core';
import { useAuth } from '@shootout-comps/core';
import { useLang } from '@shootout-comps/i18n';
import SnookerBall, { SnookerBallSpinner } from '../components/SnookerBall';
import ConfirmModal from '../components/ConfirmModal';
import ManageAdminsDialog from '../components/ManageAdminsDialog';
import { useSocketEvents, useConnectionStatus } from '../adapters/webSocket';

interface TournamentsPageProps {
  // registration/active rows — open the management view (Players/Bracket)
  onOpenTournament: (id: string, status: string) => void;
  // complete rows — open the read-only historic detail view
  onViewHistoric: (id: string) => void;
}


const MAX_OPTIONS = [2, 4, 8, 16, 32, 64, 128];

const CURRENCIES = [
  { code: 'GBP', symbol: '£',   name: 'GBP £' },
  { code: 'EUR', symbol: '€',   name: 'EUR €' },
  { code: 'USD', symbol: '$',   name: 'USD $' },
  { code: 'AUD', symbol: 'A$',  name: 'AUD A$' },
  { code: 'CAD', symbol: 'C$',  name: 'CAD C$' },
  { code: 'CHF', symbol: 'Fr',  name: 'CHF Fr' },
  { code: 'SEK', symbol: 'kr',  name: 'SEK kr' },
  { code: 'NOK', symbol: 'kr',  name: 'NOK kr' },
  { code: 'DKK', symbol: 'kr',  name: 'DKK kr' },
  { code: 'INR', symbol: '₹',   name: 'INR ₹' },
  { code: 'JPY', symbol: '¥',   name: 'JPY ¥' },
  { code: 'CNY', symbol: '¥',   name: 'CNY ¥' },
  { code: 'ZAR', symbol: 'R',   name: 'ZAR R' },
  { code: 'AED', symbol: 'د.إ', name: 'AED' },
  { code: 'SAR', symbol: '﷼',   name: 'SAR' },
];

const PLACE_LABELS: Record<number, string> = {
  1: '🥇 1st', 2: '🥈 2nd', 3: '🥉 3rd',
  4: '4th', 5: '5th', 6: '6th', 7: '7th', 8: '8th',
};
const PRIZE_PLACE_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 7, 8];

function currencySymbol(code: string): string {
  return CURRENCIES.find(c => c.code === code)?.symbol ?? code;
}

function toLocalDT(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

interface DialogData {
  name: string;
  maxPlayers: number;
  scheduledAt: string | null;
  isHandicap: boolean;
  location: string;
  entryFee: string;
  currency: string;
  prizePlaces: number;
  prizeAmounts: Record<number, string>;
  highBreakPrize: string;
  format: 'shootout' | 'snooker900' | 'standard';
  sixReds: boolean;
  isPublic: boolean;
  tournamentStyle: 'timed' | 'standard'; // top-level style picker
  bestOfByRound?: number[]; // one entry per round — standard/snooker900 only
}

const BEST_OF_OPTIONS = [1, 3, 5, 7, 9, 11, 15, 19];

// Mirrors the round-naming convention used elsewhere (e.g. BracketPage.tsx) — kept local since
// it's a small, self-contained lookup rather than a shared abstraction.
function getRoundLabel(t: ReturnType<typeof useLang>['t'], totalRounds: number, roundIdx: number): string {
  const fromEnd = totalRounds - 1 - roundIdx;
  if (fromEnd === 0) return t.roundFinal;
  if (fromEnd === 1) return t.roundSemiFinals;
  if (fromEnd === 2) return t.roundQuarterFinals;
  if (fromEnd === 3) return t.roundOf16;
  return `${t.roundOf} ${Math.pow(2, fromEnd + 1)}`;
}

// ─── Best-of-Frames-Per-Round Dialog ───────────────────────────────────────────
// Shown for standard/snooker900 tournaments only (not shootout) — lets the creator pick how
// many frames decide each round of the bracket.
interface BestOfByRoundDialogProps {
  maxPlayers: number;
  initialBestOfByRound?: number[];
  saving: boolean;
  onSave: (bestOfByRound: number[]) => void;
  onBack: () => void;
}

function BestOfByRoundDialog({ maxPlayers, initialBestOfByRound, saving, onSave, onBack }: BestOfByRoundDialogProps) {
  const { t } = useLang();
  const totalRounds = Math.max(1, Math.round(Math.log2(maxPlayers)));
  const [values, setValues] = useState<number[]>(() =>
    Array.from({ length: totalRounds }, (_, i) => initialBestOfByRound?.[i] ?? 1)
  );

  const setRound = (idx: number, val: number) => {
    setValues(prev => prev.map((v, i) => (i === idx ? val : v)));
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '1rem', overflowY: 'auto', animation: 'fadeIn 0.15s ease' }}
      onClick={e => { if (e.target === e.currentTarget) onBack(); }}
    >
      <div style={{ background: 'linear-gradient(135deg, var(--baize), var(--felt))', border: '2px solid rgba(201,168,76,0.4)', borderRadius: '16px', padding: 'clamp(1.5rem,5vw,2rem)', width: '100%', maxWidth: '500px', boxShadow: '0 24px 64px rgba(0,0,0,0.6)', animation: 'slideUp 0.2s ease', margin: 'auto' }}>
        <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '1.8rem', color: 'var(--gold)', marginBottom: '0.4rem', lineHeight: 1 }}>
          {t.bestOfDialogTitle}
        </h2>
        <p style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem', marginBottom: '1.5rem', lineHeight: 1.6 }}>
          {t.bestOfDialogDesc}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem', marginBottom: '1.5rem' }}>
          {values.map((val, idx) => (
            <div key={idx}>
              <label style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: '0.4rem', fontWeight: 600 }}>
                {getRoundLabel(t, totalRounds, idx)}
              </label>
              <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                {BEST_OF_OPTIONS.map(n => (
                  <button
                    type="button"
                    key={n}
                    onClick={() => setRound(idx, n)}
                    style={{
                      padding: '0.4rem 0.7rem', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600,
                      cursor: 'pointer', minHeight: '40px', transition: 'all 0.15s',
                      background: val === n ? 'var(--gold)' : 'rgba(255,255,255,0.07)',
                      color: val === n ? 'var(--baize-dark)' : 'var(--chalk-dim)',
                      border: `1px solid ${val === n ? 'var(--gold)' : 'rgba(255,255,255,0.12)'}`,
                    }}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button type="button" onClick={onBack} style={{ flex: 1, padding: '0.75rem', background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', minHeight: '48px' }}>
            {t.bestOfBackBtn}
          </button>
          <button type="button" disabled={saving} onClick={() => onSave(values)} style={{ flex: 2, padding: '0.75rem', background: 'linear-gradient(135deg, var(--gold), var(--gold-light))', color: 'var(--baize-dark)', border: 'none', borderRadius: '8px', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer', minHeight: '48px', opacity: saving ? 0.6 : 1 }}>
            {saving ? '…' : t.bestOfContinueBtn}
          </button>
        </div>
      </div>
      <style>{`@keyframes fadeIn{from{opacity:0}to{opacity:1}} @keyframes slideUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}

// ─── Tournament Type Picker ───────────────────────────────────────────────────
function TournamentTypePicker({ onSelectTimed, onSelectStandard, onCancel, timedPricePence = 500, standardPricePence = 250, paymentsEnabled = true }: {
  onSelectTimed: () => void;
  onSelectStandard: () => void;
  onCancel: () => void;
  timedPricePence?: number;
  standardPricePence?: number;
  paymentsEnabled?: boolean;
}) {
  const { t } = useLang();
  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onCancel(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', animation: 'fadeIn 0.15s ease' }}
    >
      <div style={{ background: 'linear-gradient(135deg, var(--baize), var(--felt))', border: '2px solid rgba(201,168,76,0.4)', borderRadius: '16px', padding: '1.75rem', width: '100%', maxWidth: '440px', boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}>
        <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '1.8rem', color: 'var(--gold)', lineHeight: 1, margin: '0 0 0.4rem' }}>
          {t.newTournamentTitle}
        </h2>
        <p style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem', marginBottom: '1.5rem', lineHeight: 1.6 }}>
          {t.chooseTournamentType}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
          <button
            onClick={onSelectTimed}
            style={{ display: 'flex', alignItems: 'center', gap: '1rem', background: 'rgba(201,168,76,0.07)', border: '1.5px solid rgba(201,168,76,0.25)', borderRadius: '12px', padding: '1rem 1.25rem', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(201,168,76,0.14)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(201,168,76,0.5)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(201,168,76,0.07)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(201,168,76,0.25)'; }}
          >
            <span style={{ fontSize: '2rem', flexShrink: 0 }}>⚡</span>
            <div>
              <div style={{ fontWeight: 700, color: 'var(--chalk)', fontSize: '0.95rem', marginBottom: '0.2rem' }}>{t.timedTournamentTitle}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)', lineHeight: 1.5 }}>{t.timedTournamentDesc}{paymentsEnabled ? ` · £${(timedPricePence / 100).toFixed(2)}` : ''}</div>
            </div>
          </button>
          <button
            onClick={onSelectStandard}
            style={{ display: 'flex', alignItems: 'center', gap: '1rem', background: 'rgba(52,152,219,0.07)', border: '1.5px solid rgba(52,152,219,0.25)', borderRadius: '12px', padding: '1rem 1.25rem', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(52,152,219,0.14)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(52,152,219,0.5)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(52,152,219,0.07)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(52,152,219,0.25)'; }}
          >
            <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}><SnookerBall size={32} /></span>
            <div>
              <div style={{ fontWeight: 700, color: 'var(--chalk)', fontSize: '0.95rem', marginBottom: '0.2rem' }}>{t.standardTournamentTitle}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)', lineHeight: 1.5 }}>{t.standardTournamentDesc}{paymentsEnabled ? ` · £${(standardPricePence / 100).toFixed(2)}` : ''}</div>
            </div>
          </button>
        </div>
        <button onClick={onCancel} style={{ width: '100%', padding: '0.65rem', background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', fontSize: '0.85rem', cursor: 'pointer' }}>
          {t.cancelBtn}
        </button>
      </div>
      <style>{`@keyframes fadeIn{from{opacity:0}to{opacity:1}}`}</style>
    </div>
  );
}

interface TournamentDialogProps {
  mode: 'new' | 'edit';
  initialName?: string;
  initialMaxPlayers?: number;
  initialScheduledAt?: string | null;
  initialIsHandicap?: boolean;
  initialLocation?: string | null;
  initialEntryFee?: number | null;
  initialCurrency?: string;
  initialPrizes?: { place: number; amount: number }[];
  initialHighBreakPrize?: number | null;
  initialFormat?: 'shootout' | 'snooker900' | 'standard';
  initialSixReds?: boolean;
  initialIsPublic?: boolean;
  initialStyle?: 'timed' | 'standard'; // 'standard' hides format selector
  saving: boolean;
  onSave: (data: DialogData) => void;
  onCancel: () => void;
}

function TournamentDialog({
  mode,
  initialName = '',
  initialMaxPlayers = 32,
  initialScheduledAt,
  initialIsHandicap = false,
  initialLocation,
  initialEntryFee,
  initialCurrency = 'GBP',
  initialPrizes = [],
  initialHighBreakPrize,
  initialFormat = 'shootout',
  initialSixReds = false,
  initialIsPublic = false,
  initialStyle = 'timed',
  saving,
  onSave,
  onCancel,
}: TournamentDialogProps) {
  const { t } = useLang();
  const [name, setName]               = useState(initialName);
  const [maxP, setMaxP]               = useState(initialMaxPlayers);
  const [schedAt, setSchedAt]         = useState(toLocalDT(initialScheduledAt));
  const [isHandicap, setIsHandicap]   = useState(initialIsHandicap);
  const [isPublic, setIsPublic]       = useState(initialIsPublic);
  const [location, setLocation]       = useState(initialLocation ?? '');
  const [entryFee, setEntryFee]       = useState(initialEntryFee != null ? String(initialEntryFee) : '');
  const [currency, setCurrency]       = useState(initialCurrency);
  const [prizePlaces, setPrizePlaces] = useState(initialPrizes.length);
  const [highBreakPrize, setHighBreakPrize] = useState(initialHighBreakPrize != null ? String(initialHighBreakPrize) : '');
  const isStandardStyle = initialStyle === 'standard';
  const [format, setFormat]           = useState<'shootout' | 'snooker900' | 'standard'>(isStandardStyle ? 'standard' : (initialFormat as 'shootout' | 'snooker900') ?? 'shootout');
  const [sixReds, setSixReds]           = useState(initialSixReds ?? false);
  const [prizeAmounts, setPrizeAmounts] = useState<Record<number, string>>(() => {
    const init: Record<number, string> = {};
    initialPrizes.forEach((p: { place: number; amount: number }) => { init[p.place] = String(p.amount); });
    return init;
  });

  const inp: React.CSSProperties = {
    width: '100%', padding: '0.7rem 0.9rem', background: 'rgba(0,0,0,0.35)',
    border: '1.5px solid rgba(201,168,76,0.3)', borderRadius: '8px',
    color: 'var(--chalk)', fontSize: '0.92rem', outline: 'none', boxSizing: 'border-box',
  };
  const lbl: React.CSSProperties = {
    fontSize: '0.72rem', color: 'var(--chalk-dim)', textTransform: 'uppercase',
    letterSpacing: '0.1em', display: 'block', marginBottom: '0.4rem', fontWeight: 600,
  };
  const hint: React.CSSProperties = { fontSize: '0.68rem', color: 'var(--chalk-dim)', marginTop: '0.3rem' };

  const sym = currencySymbol(currency);

  const handlePrizePlacesChange = (n: number) => {
    setPrizePlaces(n);
    setPrizeAmounts(prev => {
      const next = { ...prev };
      for (let i = 1; i <= n; i++) { if (!next[i]) next[i] = ''; }
      return next;
    });
  };

  const prizeTotal = Array.from({ length: prizePlaces }, (_, i) =>
    parseFloat(prizeAmounts[i + 1] ?? '0') || 0
  ).reduce((a, b) => a + b, 0);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({ name: name.trim(), maxPlayers: maxP, scheduledAt: schedAt ? new Date(schedAt).toISOString() : null, isHandicap, location, entryFee, currency, prizePlaces, prizeAmounts, highBreakPrize, format, sixReds, isPublic, tournamentStyle: isStandardStyle ? 'standard' : 'timed' });
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '1rem', overflowY: 'auto', animation: 'fadeIn 0.15s ease' }}
      onClick={e => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div style={{ background: 'linear-gradient(135deg, var(--baize), var(--felt))', border: '2px solid rgba(201,168,76,0.4)', borderRadius: '16px', padding: 'clamp(1.5rem,5vw,2rem)', width: '100%', maxWidth: '500px', boxShadow: '0 24px 64px rgba(0,0,0,0.6)', animation: 'slideUp 0.2s ease', margin: 'auto' }}>
        <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '1.8rem', color: 'var(--gold)', marginBottom: '1.5rem', lineHeight: 1 }}>
          {mode === 'new' ? t.newTournamentTitle : t.editTournamentTitle}
        </h2>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>

          {/* Name */}
          <div>
            <label style={lbl}>{t.tournamentNameLabel}</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Winter Shootout 2025" style={inp} required />
          </div>

          {/* Date & time */}
          <div>
            <label style={lbl}>{t.dateTimeLabel}</label>
            <input type="datetime-local" value={schedAt} onChange={e => setSchedAt(e.target.value)} style={{ ...inp, colorScheme: 'dark' }} />
            <p style={hint}>{t.dateTimeHint}</p>
          </div>

          {/* Location */}
          <div>
            <label style={lbl}>{t.locationLabel}</label>
            <input value={location} onChange={e => setLocation(e.target.value)} placeholder={t.locationPlaceholder} style={inp} />
            <p style={hint}>{t.locationHint}</p>
          </div>

          {/* Entry fee */}
          <div>
            <label style={lbl}>{t.entryFeeLabel}</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <select value={currency} onChange={e => setCurrency(e.target.value)} style={{ ...inp, width: 'auto', flex: 'none', minWidth: '105px', cursor: 'pointer' }}>
                {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
              <input type="number" min="0" step="0.01" value={entryFee} onChange={e => setEntryFee(e.target.value)} placeholder="0.00" style={{ ...inp, flex: 1 }} />
            </div>
            <p style={hint}>{t.entryFeeHint}</p>
          </div>

          {/* Prize places */}
          <div>
            <label style={lbl}>{t.prizePlacesLabel}</label>
            <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginBottom: '0.65rem' }}>
              {PRIZE_PLACE_OPTIONS.map(n => (
                <button type="button" key={n} onClick={() => handlePrizePlacesChange(n)} style={{ padding: '0.35rem 0.65rem', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', minHeight: '36px', transition: 'all 0.15s', background: prizePlaces === n ? 'var(--gold)' : 'rgba(255,255,255,0.07)', color: prizePlaces === n ? 'var(--baize-dark)' : 'var(--chalk-dim)', border: `1px solid ${prizePlaces === n ? 'var(--gold)' : 'rgba(255,255,255,0.12)'}` }}>
                  {n === 0 ? t.prizePlacesNone : n}
                </button>
              ))}
            </div>
            {prizePlaces > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                {Array.from({ length: prizePlaces }, (_, i) => i + 1).map(place => (
                  <div key={place} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                    <span style={{ minWidth: '52px', fontSize: '0.85rem', fontWeight: 600, color: place <= 3 ? 'var(--gold)' : 'var(--chalk-dim)' }}>
                      {PLACE_LABELS[place] ?? `${place}th`}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', flex: 1, background: 'rgba(0,0,0,0.35)', borderRadius: '8px', border: '1.5px solid rgba(201,168,76,0.3)', overflow: 'hidden' }}>
                      <span style={{ padding: '0 0.6rem', color: 'var(--chalk-dim)', fontSize: '0.88rem', borderRight: '1px solid rgba(201,168,76,0.2)', userSelect: 'none', alignSelf: 'stretch', display: 'flex', alignItems: 'center' }}>{sym}</span>
                      <input type="number" min="0" step="0.01" value={prizeAmounts[place] ?? ''} onChange={e => setPrizeAmounts(prev => ({ ...prev, [place]: e.target.value }))} placeholder="0.00" style={{ flex: 1, padding: '0.6rem 0.75rem', background: 'transparent', border: 'none', color: 'var(--chalk)', fontSize: '0.92rem', outline: 'none' }} />
                    </div>
                  </div>
                ))}
                <p style={{ ...hint, marginTop: '0.2rem' }}>
                  {t.prizePoolLabel}: <span style={{ color: 'var(--gold)', fontWeight: 600 }}>{sym}{prizeTotal.toFixed(2)}</span>
                </p>
              </div>
            )}
{prizePlaces === 0 && <p style={hint}>{t.prizePlacesHint}</p>}
          </div>

          {/* High break prize */}
          <div>
            <label style={lbl}>{t.highBreakPrizeLabel}</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.75rem', background: 'rgba(0,0,0,0.25)', border: '1.5px solid rgba(201,168,76,0.25)', borderRight: 'none', borderRadius: '8px 0 0 8px', color: 'var(--chalk-dim)', fontSize: '0.9rem' }}>
                {sym}
              </span>
              <input
                type="number" min="0" step="0.01"
                value={highBreakPrize}
                onChange={e => setHighBreakPrize(e.target.value)}
                placeholder="0.00"
                style={{ ...inp, borderRadius: '0 8px 8px 0', borderLeft: 'none', flex: 1 }}
              />
            </div>
            <p style={hint}>{t.highBreakPrizeHint}</p>
          </div>

          {/* Max players */}
          <div>
            <label style={lbl}>{t.maxPlayersLabel}</label>
            <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
              {MAX_OPTIONS.map(n => (
                <button type="button" key={n} onClick={() => setMaxP(n)} style={{ padding: '0.4rem 0.75rem', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', minHeight: '40px', transition: 'all 0.15s', background: maxP === n ? 'var(--gold)' : 'rgba(255,255,255,0.07)', color: maxP === n ? 'var(--baize-dark)' : 'var(--chalk-dim)', border: `1px solid ${maxP === n ? 'var(--gold)' : 'rgba(255,255,255,0.12)'}` }}>
                  {n}
                </button>
              ))}
            </div>
            <p style={hint}>{t.maxPlayersHint}</p>
          </div>

          {/* Format — hidden for standard tournaments */}
          {!isStandardStyle && (
          <div>
            <label style={lbl}>{t.matchFormatLabel}</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {(['shootout', 'snooker900'] as const).map(f => (
                <button
                  type="button"
                  key={f}
                  onClick={() => setFormat(f)}
                  style={{
                    flex: 1, padding: '0.7rem 0.5rem', borderRadius: '8px', cursor: 'pointer',
                    fontWeight: 600, fontSize: '0.85rem', transition: 'all 0.15s', minHeight: '44px',
                    background: format === f ? 'rgba(201,168,76,0.18)' : 'rgba(255,255,255,0.06)',
                    color: format === f ? 'var(--gold)' : 'var(--chalk-dim)',
                    border: `1.5px solid ${format === f ? 'var(--gold)' : 'rgba(255,255,255,0.12)'}`,
                  }}
                >
                  {f === 'shootout' ? '⚡ Shootout' : '🕐 Snooker 900'}
                </button>
              ))}
            </div>
            <p style={hint}>
              {format === 'shootout'
                ? t.shootoutDesc.replace('{r}', sixReds ? '6' : '15')
                : t.snooker900Desc.replace('{r}', sixReds ? '6' : '15')}
            </p>
          </div>
          )}

          {/* Six Reds toggle — shown for all formats */}
          <div>
            {isStandardStyle && (
              <p style={{ ...hint, marginBottom: '0.5rem' }}>
                {t.standardFormatHint.replace('{r}', sixReds ? '6' : '15')}
              </p>
            )}
            <button
              type="button"
              onClick={() => setSixReds(!sixReds)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.6rem', marginTop: '0.5rem',
                background: sixReds ? 'rgba(192,57,43,0.12)' : 'rgba(255,255,255,0.06)',
                border: `1px solid ${sixReds ? 'rgba(192,57,43,0.4)' : 'rgba(255,255,255,0.15)'}`,
                borderRadius: '8px', padding: '0.55rem 0.9rem', cursor: 'pointer',
                fontSize: '0.85rem', color: sixReds ? '#e74c3c' : 'var(--chalk-dim)',
                fontWeight: 600, minHeight: '44px', transition: 'all 0.2s', width: '100%',
              }}
            >
              <span style={{ width: '34px', height: '20px', borderRadius: '10px', flexShrink: 0, background: sixReds ? '#e74c3c' : 'rgba(255,255,255,0.15)', display: 'inline-flex', alignItems: 'center', padding: '2px', transition: 'background 0.2s' }}>
                <span style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#fff', transform: sixReds ? 'translateX(14px)' : 'translateX(0)', transition: 'transform 0.2s', display: 'block', flexShrink: 0 }} />
              </span>
              {t.sixRedsLabel}
            </button>
          </div>

          {/* Handicap toggle */}
          <div>
            <label style={lbl}>{t.handicapLabel}</label>
            <button type="button" onClick={() => setIsHandicap(!isHandicap)} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', background: isHandicap ? 'rgba(46,204,113,0.12)' : 'rgba(255,255,255,0.06)', border: `1px solid ${isHandicap ? 'rgba(46,204,113,0.4)' : 'rgba(255,255,255,0.15)'}`, borderRadius: '8px', padding: '0.55rem 0.9rem', cursor: 'pointer', fontSize: '0.85rem', color: isHandicap ? '#2ecc71' : 'var(--chalk-dim)', fontWeight: 600, minHeight: '44px', transition: 'all 0.2s', width: '100%' }}>
              <span style={{ width: '34px', height: '20px', borderRadius: '10px', flexShrink: 0, background: isHandicap ? '#2ecc71' : 'rgba(255,255,255,0.15)', display: 'inline-flex', alignItems: 'center', padding: '2px', transition: 'background 0.2s' }}>
                <span style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#fff', transform: isHandicap ? 'translateX(14px)' : 'translateX(0)', transition: 'transform 0.2s', display: 'block', flexShrink: 0 }} />
              </span>
              {isHandicap ? t.handicapOnDesc : t.handicapOffDesc}
            </button>
            <p style={hint}>{t.handicapHint}</p>
          </div>

          {/* Public tournament toggle */}
          <div>
            <label style={lbl}>{t.makePublicLabel}</label>
            <button type="button" onClick={() => setIsPublic(!isPublic)} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', background: isPublic ? 'rgba(52,152,219,0.12)' : 'rgba(255,255,255,0.06)', border: `1px solid ${isPublic ? 'rgba(52,152,219,0.4)' : 'rgba(255,255,255,0.15)'}`, borderRadius: '8px', padding: '0.55rem 0.9rem', cursor: 'pointer', fontSize: '0.85rem', color: isPublic ? '#5dade2' : 'var(--chalk-dim)', fontWeight: 600, minHeight: '44px', transition: 'all 0.2s', width: '100%' }}>
              <span style={{ width: '34px', height: '20px', borderRadius: '10px', flexShrink: 0, background: isPublic ? '#5dade2' : 'rgba(255,255,255,0.15)', display: 'inline-flex', alignItems: 'center', padding: '2px', transition: 'background 0.2s' }}>
                <span style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#fff', transform: isPublic ? 'translateX(14px)' : 'translateX(0)', transition: 'transform 0.2s', display: 'block', flexShrink: 0 }} />
              </span>
              🌐 {t.makePublicLabel}
            </button>
            <p style={hint}>{t.makePublicHint}</p>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.25rem' }}>
            <button type="button" onClick={onCancel} style={{ flex: 1, padding: '0.75rem', background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', minHeight: '48px' }}>
              {t.cancelBtn}
            </button>
            <button type="submit" disabled={saving || !name.trim()} style={{ flex: 2, padding: '0.75rem', background: 'linear-gradient(135deg, var(--gold), var(--gold-light))', color: 'var(--baize-dark)', border: 'none', borderRadius: '8px', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer', minHeight: '48px', opacity: saving || !name.trim() ? 0.6 : 1 }}>
              {saving ? '…' : mode === 'new' ? t.createTournament : t.saveChanges}
            </button>
          </div>
        </form>
      </div>
      <style>{`@keyframes fadeIn{from{opacity:0}to{opacity:1}} @keyframes slideUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}

// ─── Poster Modal ─────────────────────────────────────────────────────────────
interface PosterModalProps {
  tournament: {
    name: string;
    location?: string | null;
    scheduledAt?: string | null;
    entryFee?: number | null;
    currency?: string;
    prizes?: { place: number; amount: number }[];
    maxPlayers?: number;
    isHandicap?: boolean;
  };
  onClose: () => void;
}

function PosterModal({ tournament, onClose }: PosterModalProps) {
  const { t } = useLang();
  const [extraText, setExtraText] = useState('');
  const [generating, setGenerating] = useState(false);

  const inp: React.CSSProperties = {
    width: '100%', padding: '0.7rem 0.9rem', background: 'rgba(0,0,0,0.35)',
    border: '1.5px solid rgba(201,168,76,0.3)', borderRadius: '8px',
    color: 'var(--chalk)', fontSize: '0.92rem', outline: 'none', boxSizing: 'border-box',
  };
  const lbl: React.CSSProperties = {
    fontSize: '0.72rem', color: 'var(--chalk-dim)', textTransform: 'uppercase',
    letterSpacing: '0.1em', display: 'block', marginBottom: '0.4rem', fontWeight: 600,
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      // Dynamic import so jspdf is only loaded when needed
      const { generatePoster: gen } = await import('../utils/generatePoster');
      gen({
        name: tournament.name,
        location: tournament.location,
        scheduledAt: tournament.scheduledAt,
        entryFee: tournament.entryFee,
        currency: tournament.currency,
        prizes: tournament.prizes,
        highBreakPrize: (tournament as unknown as { highBreakPrize?: number | null }).highBreakPrize,
        maxPlayers: tournament.maxPlayers,
        isHandicap: tournament.isHandicap,
        format: (tournament as unknown as { format?: 'shootout' | 'snooker900' | 'standard' }).format,
        sixReds: (tournament as unknown as { sixReds?: boolean }).sixReds,
        extraText,
      });
    } finally {
      setGenerating(false);
    }
  };

  const sym = (code?: string) => {
    const map: Record<string, string> = { GBP: '£', EUR: '€', USD: '$', AUD: 'A$', CAD: 'C$', CHF: 'Fr', SEK: 'kr', NOK: 'kr', DKK: 'kr', INR: '₹', JPY: '¥', CNY: '¥', ZAR: 'R' };
    return map[code ?? 'GBP'] ?? (code ?? '');
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '1rem', overflowY: 'auto', animation: 'fadeIn 0.15s ease' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: 'linear-gradient(135deg, var(--baize), var(--felt))', border: '2px solid rgba(201,168,76,0.4)', borderRadius: '16px', padding: 'clamp(1.5rem,5vw,2rem)', width: '100%', maxWidth: '480px', boxShadow: '0 24px 64px rgba(0,0,0,0.6)', animation: 'slideUp 0.2s ease', margin: 'auto' }}>

        <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '1.8rem', color: 'var(--gold)', marginBottom: '0.4rem', lineHeight: 1 }}>
          🖨 Generate Poster
        </h2>
        <p style={{ fontSize: '0.8rem', color: 'var(--chalk-dim)', marginBottom: '1.5rem' }}>
          Creates a printable A4 PDF poster for this tournament.
        </p>

        {/* Preview of what will be on the poster */}
        <div style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '10px', padding: '1rem', marginBottom: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          <div style={{ fontSize: '0.65rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.2rem', fontWeight: 600 }}>Poster will include</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--gold)' }}>{tournament.name}</div>
          {tournament.scheduledAt && (
            <div style={{ fontSize: '0.8rem', color: 'var(--chalk-dim)' }}>
              📅 {new Date(tournament.scheduledAt).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} at {new Date(tournament.scheduledAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
            </div>
          )}
          {tournament.location && <div style={{ fontSize: '0.8rem', color: 'var(--chalk-dim)' }}>📍 {tournament.location}</div>}
          {tournament.entryFee != null
            ? <div style={{ fontSize: '0.8rem', color: 'var(--chalk-dim)' }}>💰 Entry: {sym(tournament.currency)}{tournament.entryFee}</div>
            : <div style={{ fontSize: '0.8rem', color: 'var(--chalk-dim)' }}>💰 Free Entry</div>
          }
          {tournament.prizes && tournament.prizes.length > 0 && (
            <div style={{ fontSize: '0.8rem', color: 'var(--chalk-dim)' }}>
              🏆 {tournament.prizes.length} prize {tournament.prizes.length === 1 ? 'place' : 'places'} · Pool: {sym(tournament.currency)}{tournament.prizes.reduce((s, p) => s + p.amount, 0).toFixed(2)}
            </div>
          )}
          {(tournament as unknown as { highBreakPrize?: number | null }).highBreakPrize != null && ((tournament as unknown as { highBreakPrize?: number | null }).highBreakPrize ?? 0) > 0 && (
            <div style={{ fontSize: '0.8rem', color: 'var(--chalk-dim)' }}>
              🔴 {t.highBreakPrizeDisplay}: {sym(tournament.currency)}{(tournament as unknown as { highBreakPrize: number }).highBreakPrize.toFixed(2)}
            </div>
          )}
          {tournament.maxPlayers && <div style={{ fontSize: '0.8rem', color: 'var(--chalk-dim)' }}>👥 Max {tournament.maxPlayers} entries</div>}
          <div style={{ fontSize: '0.8rem', color: 'var(--chalk-dim)' }}>🔴 {tournament.isHandicap ? t.handicapTournament : t.scratchTournament}</div>
          {!tournament.scheduledAt && !tournament.location && tournament.entryFee == null && (!tournament.prizes || tournament.prizes.length === 0) && (
            <div style={{ fontSize: '0.78rem', color: '#e67e22' }}>
              ⚠ {t.noDateVenueWarning}
            </div>
          )}
        </div>

        {/* Extra text */}
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={lbl}>{t.additionalTextLabel}</label>
          <textarea
            value={extraText}
            onChange={e => setExtraText(e.target.value)}
            placeholder={t.additionalTextPlaceholder}
            rows={3}
            style={{ ...inp, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }}
          />
          <p style={{ fontSize: '0.68rem', color: 'var(--chalk-dim)', marginTop: '0.3rem' }}>
{t.additionalTextHint}
          </p>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button onClick={onClose} style={{ flex: 1, padding: '0.75rem', background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', minHeight: '48px' }}>
            {t.cancelBtn}
          </button>
          <button onClick={handleGenerate} disabled={generating} style={{ flex: 2, padding: '0.75rem', background: 'linear-gradient(135deg, var(--gold), var(--gold-light))', color: 'var(--baize-dark)', border: 'none', borderRadius: '8px', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer', minHeight: '48px', opacity: generating ? 0.7 : 1 }}>
            {generating ? t.generatingPoster : t.downloadPdfPoster}
          </button>
        </div>
      </div>
      <style>{`@keyframes fadeIn{from{opacity:0}to{opacity:1}} @keyframes slideUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}


// ─── Payment Gate Modal ───────────────────────────────────────────────────────
interface PaymentGateProps {
  onPay: (stripeSessionId: string) => void;
  onCancel: () => void;
  processing: boolean;
  error: string;
  amountPence?: number;
  format?: string;
}

function PaymentGate({ onPay, onCancel, processing, error, amountPence = 500, format }: PaymentGateProps) {
  const { t } = useLang();
  const { getAccessToken } = useAuth();
  const amountStr = `£${(amountPence / 100).toFixed(2)}`;

  const handlePay = async () => {
    const token = await getAccessToken();
    const res = await fetch('/api/tournament/create-checkout-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ format }),
    });
    const data = await res.json() as { url?: string; mockSessionId?: string; isMock?: boolean; error?: string };

    if (data.mockSessionId) {
      // Mock mode — Stripe not configured on server (dev/test without STRIPE_SECRET_KEY)
      onPay(data.mockSessionId);
    } else if (data.url) {
      // Real Stripe Checkout — redirect the user
      window.location.href = data.url;
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '1rem', overflowY: 'auto', animation: 'fadeIn 0.15s ease' }}
      onClick={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div style={{ background: 'linear-gradient(135deg, var(--baize), var(--felt))', border: '2px solid rgba(201,168,76,0.4)', borderRadius: '16px', padding: 'clamp(1.5rem,5vw,2rem)', width: '100%', maxWidth: '420px', boxShadow: '0 24px 64px rgba(0,0,0,0.6)', animation: 'slideUp 0.2s ease', margin: 'auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🏆</div>
          <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '1.8rem', color: 'var(--gold)', lineHeight: 1, margin: 0 }}>{t.paymentGateTitle}</h2>
          <p style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem', marginTop: '0.5rem', lineHeight: 1.6 }}>
            {t.paymentGateDesc.replace('{amount}', amountStr)}
          </p>
        </div>

        <div style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '10px', padding: '1rem', marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--chalk-dim)' }}>New Tournament</span>
            <span style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--chalk)' }}>{amountStr}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '0.4rem', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--chalk)' }}>Total</span>
            <span style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--gold)' }}>{amountStr}</span>
          </div>
        </div>

        {error && (
          <div style={{ background: 'rgba(192,57,43,0.15)', border: '1px solid rgba(192,57,43,0.4)', borderRadius: '8px', padding: '0.65rem 1rem', color: '#e74c3c', fontSize: '0.82rem', marginBottom: '1rem' }}>
            ⚠ {error}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          <button
            onClick={handlePay}
            disabled={processing}
            style={{ width: '100%', padding: '0.85rem', background: 'linear-gradient(135deg, var(--gold), var(--gold-light))', color: 'var(--baize-dark)', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: 700, cursor: 'pointer', minHeight: '52px', opacity: processing ? 0.7 : 1 }}
          >
            {processing ? '⏳ Processing…' : `💳 Pay ${amountStr} & Create Tournament`}
          </button>
          <button
            onClick={onCancel}
            disabled={processing}
            style={{ width: '100%', padding: '0.75rem', background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', fontSize: '0.9rem', cursor: 'pointer', minHeight: '44px' }}
          >
            Cancel
          </button>
        </div>

        <p style={{ fontSize: '0.68rem', color: 'var(--chalk-dim)', textAlign: 'center', marginTop: '0.75rem', lineHeight: 1.5 }}>
          🔒 Secure payment via Stripe · One-off payment per tournament
        </p>
      </div>
      <style>{`@keyframes fadeIn{from{opacity:0}to{opacity:1}} @keyframes slideUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}

function dialogDataToSettings(data: DialogData) {
  return {
    maxPlayers:     data.maxPlayers,
    scheduledAt:    data.scheduledAt,
    isHandicap:     data.isHandicap,
    location:       data.location.trim() || null,
    entryFee:       data.entryFee !== '' ? parseFloat(data.entryFee) || null : null,
    currency:       data.currency,
    prizes:         Array.from({ length: data.prizePlaces }, (_, i) => ({
      place: i + 1,
      amount: parseFloat(data.prizeAmounts[i + 1] ?? '0') || 0,
    })),
    highBreakPrize: data.highBreakPrize !== '' ? parseFloat(data.highBreakPrize) || null : null,
    format:         data.format,
    sixReds:        data.sixReds,
    isPublic:       data.isPublic,
    bestOfByRound:  data.format !== 'shootout' ? (data.bestOfByRound ?? null) : null,
  };
}

// Maps a previously-collected DialogData back to TournamentDialog's initial* props — used to
// restore what the user already entered when they step back from the best-of-frames dialog.
function dialogDataToInitialProps(data: DialogData) {
  return {
    initialName: data.name,
    initialMaxPlayers: data.maxPlayers,
    initialScheduledAt: data.scheduledAt,
    initialIsHandicap: data.isHandicap,
    initialLocation: data.location,
    initialEntryFee: data.entryFee !== '' ? parseFloat(data.entryFee) || null : null,
    initialCurrency: data.currency,
    initialPrizes: Array.from({ length: data.prizePlaces }, (_, i) => ({
      place: i + 1,
      amount: parseFloat(data.prizeAmounts[i + 1] ?? '0') || 0,
    })),
    initialHighBreakPrize: data.highBreakPrize !== '' ? parseFloat(data.highBreakPrize) || null : null,
    initialFormat: data.format,
    initialSixReds: data.sixReds,
    initialIsPublic: data.isPublic,
  };
}

export default function TournamentsPage({ onOpenTournament, onViewHistoric }: TournamentsPageProps) {
  const { fetchTournamentHistory, fetchHistoricTournament, createTournament, cancelTournament, updateName, updateSettings, pauseAllClocks, resumeAllClocks, getPaymentStatus, confirmPayment } = useTournament();
  const { user } = useAuth();
  const { t } = useLang();
  const STATUS_LABEL: Record<string, { label: string; colour: string }> = {
    registration: { label: t.statusRegistrationLabel, colour: '#3498db' },
    active:       { label: t.statusActiveLabel,        colour: '#2ecc71' },
    complete:     { label: t.statusCompleteLabel,      colour: 'var(--gold)' },
  };

  const [history, setHistory]               = useState<TournamentSummary[] | null>(null);
  const [showPaymentGate, setShowPaymentGate] = useState(false);
  const [pendingPaymentData, setPendingPaymentData] = useState<DialogData | null>(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentError, setPaymentError]     = useState('');
  const [paymentAmountPence, setPaymentAmountPence] = useState(500);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState<string | null>(null);
  const [showNew, setShowNew]               = useState<false | 'timed' | 'standard'>(false);
  const [showTypePicker, setShowTypePicker] = useState(false);
  const [standardEnabled, setStandardEnabled] = useState(false);
  const [timedPricePence, setTimedPricePence]     = useState(500);
  const [standardPricePence, setStandardPricePence] = useState(250);
  const [paymentsEnabled, setPaymentsEnabled] = useState(true);
  const [showEdit, setShowEdit]             = useState(false);
  const [editingTournament, setEditingTournament] = useState<Awaited<ReturnType<typeof fetchHistoricTournament>> | null>(null);
  const [dialogSaving, setDialogSaving]     = useState(false);
  const [showBestOfNew, setShowBestOfNew]   = useState(false);
  const [pendingNewDialogData, setPendingNewDialogData] = useState<DialogData | null>(null);
  const [showBestOfEdit, setShowBestOfEdit] = useState(false);
  const [pendingEditDialogData, setPendingEditDialogData] = useState<DialogData | null>(null);
  const [pausingId, setPausingId]           = useState<string | null>(null);
  const [pausedIds, setPausedIds]           = useState<Set<string>>(new Set());
  const [togglingPublicId, setTogglingPublicId] = useState<string | null>(null);
  const [posterTournament, setPosterTournament] = useState<Awaited<ReturnType<typeof fetchHistoricTournament>> | null>(null);
  const [cancelTarget, setCancelTarget]     = useState<TournamentSummary | null>(null);
  const [cancelling, setCancelling]         = useState(false);
  const [cancelError, setCancelError]       = useState('');
  const [adminsTarget, setAdminsTarget]     = useState<TournamentSummary | null>(null);

  const reload = () => {
    setLoading(true);
    fetchTournamentHistory()
      .then(setHistory)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  };

  // Live updates: creating/cancelling/starting/renaming a tournament from another device or
  // browser tab shows up here immediately, no manual refresh needed.
  useSocketEvents({ 'tournaments:changed': reload });

  // Reconnect resync — catch up on anything missed while disconnected.
  const { reconnectCount } = useConnectionStatus();
  useEffect(() => { if (reconnectCount > 0) reload(); }, [reconnectCount]);

  useEffect(() => {
    reload();
    // Fetch whether standard tournaments are enabled (public setting)
    fetch('/api/settings/public')
      .then(r => r.json())
      .then((d: { standardTournamentsEnabled?: boolean; timedPricePence?: number; standardPricePence?: number; paymentsEnabled?: boolean }) => {
        setStandardEnabled(Boolean(d.standardTournamentsEnabled));
        if (d.timedPricePence !== undefined)    setTimedPricePence(d.timedPricePence);
        if (d.standardPricePence !== undefined) setStandardPricePence(d.standardPricePence);
        setPaymentsEnabled(typeof d.paymentsEnabled === 'boolean' ? d.paymentsEnabled : true);
      })
      .catch(() => { /* ignore — feature silently disabled if endpoint unavailable */ });
  }, []); // eslint-disable-line

  const doCreateTournament = async (data: DialogData, paymentToken?: string) => {
    setDialogSaving(true);
    try {
      const created = await completeTournamentCreation({
        createTournament, updateName, updateSettings,
        paymentToken, name: data.name, settings: dialogDataToSettings(data),
      });
      reload();
      // Straight into managing the new tournament (add players) — it's always fresh
      // registration status right after creation.
      onOpenTournament(created._id, 'registration');
    } finally { setDialogSaving(false); setPendingPaymentData(null); setPendingNewDialogData(null); }
  };

  // Standard/snooker900 tournaments get an extra step to pick best-of-frames per round;
  // shootout tournaments skip straight to the existing save flow (matches are always 1 frame).
  const handleNewDialogSubmit = (data: DialogData) => {
    if (data.format === 'shootout') { void handleNewSave(data); return; }
    setPendingNewDialogData(data);
    setShowBestOfNew(true);
  };

  const handleBestOfNewSave = (bestOfByRound: number[]) => {
    if (!pendingNewDialogData) return;
    setShowBestOfNew(false);
    void handleNewSave({ ...pendingNewDialogData, bestOfByRound });
  };

  const handleEditDialogSubmit = (data: DialogData) => {
    if (data.format === 'shootout') { void handleEditSave(data); return; }
    setPendingEditDialogData(data);
    setShowBestOfEdit(true);
  };

  const handleBestOfEditSave = (bestOfByRound: number[]) => {
    if (!pendingEditDialogData) return;
    setShowBestOfEdit(false);
    void handleEditSave({ ...pendingEditDialogData, bestOfByRound });
  };

  const handleNewSave = async (data: DialogData) => {
    setShowNew(false);
    try {
      const status = await getPaymentStatus(data.format);
      if (status.paymentRequired) {
        sessionStorage.setItem('pendingTournament', JSON.stringify({
          name:     data.name,
          settings: dialogDataToSettings(data),
        }));
        setPaymentAmountPence(status.amountPence ?? 500);
        setPendingPaymentData(data);
        setShowPaymentGate(true);
        return;
      }
    } catch { /* fall through to direct creation */ }
    void doCreateTournament(data);
  };

  const handlePaymentSuccess = async (stripeSessionId: string) => {
    if (!pendingPaymentData) return;
    setPaymentProcessing(true); setPaymentError('');
    try {
      const { paymentToken } = await confirmPayment(stripeSessionId);
      setShowPaymentGate(false);
      await doCreateTournament(pendingPaymentData, paymentToken);
    } catch (e) {
      setPaymentError(e instanceof Error ? e.message : 'Payment failed');
    } finally { setPaymentProcessing(false); }
  };

  const openEdit = async (id: string) => {
    try {
      const full = await fetchHistoricTournament(id);
      setEditingTournament(full);
      setShowEdit(true);
    } catch { /* ignore — user can retry */ }
  };

  const handleEditSave = async (data: DialogData) => {
    if (!editingTournament) return;
    setDialogSaving(true);
    try {
      await updateName(editingTournament._id, data.name);
      await updateSettings(editingTournament._id, dialogDataToSettings(data));
      setShowEdit(false);
      setEditingTournament(null);
      reload();
    } finally { setDialogSaving(false); setPendingEditDialogData(null); }
  };

  const openPoster = async (id: string) => {
    try {
      const full = await fetchHistoricTournament(id);
      setPosterTournament(full);
    } catch { /* ignore — user can retry */ }
  };

  const handlePauseRow = async (id: string) => {
    setPausingId(id);
    try {
      if (pausedIds.has(id)) {
        await resumeAllClocks(id);
        setPausedIds(prev => { const next = new Set(prev); next.delete(id); return next; });
      } else {
        await pauseAllClocks(id);
        setPausedIds(prev => new Set(prev).add(id));
      }
    } finally { setPausingId(null); }
  };

  const handleTogglePublic = async (id: string, currentIsPublic: boolean) => {
    setTogglingPublicId(id);
    try {
      await updateSettings(id, { isPublic: !currentIsPublic });
      reload();
    } finally { setTogglingPublicId(null); }
  };

  const handleCancelConfirmed = async () => {
    if (!cancelTarget) return;
    setCancelling(true); setCancelError('');
    try {
      await cancelTournament(cancelTarget._id);
      setCancelTarget(null);
      reload();
    } catch (e) {
      setCancelError(e instanceof Error ? e.message : 'Failed to cancel');
    } finally { setCancelling(false); }
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 64px)', color: 'var(--chalk-dim)' }}>
      <div style={{ textAlign: 'center' }}><SnookerBallSpinner size={36} /><p style={{ marginTop: '0.5rem' }}>Loading…</p></div>
      <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (error) return (
    <div style={{ maxWidth: '640px', margin: '2rem auto', padding: '0 1rem' }}>
      <div style={{ background: 'rgba(192,57,43,0.15)', border: '1px solid var(--red)', borderRadius: '8px', padding: '1rem', color: '#e74c3c' }}>Failed to load: {error}</div>
    </div>
  );

  const tournaments = history ?? [];
  const editAny = editingTournament as unknown as { location?: string | null; entryFee?: number | null; currency?: string; prizes?: { place: number; amount: number }[]; isHandicap?: boolean; format?: 'shootout' | 'snooker900' | 'standard'; sixReds?: boolean; isPublic?: boolean; maxPlayers?: number; scheduledAt?: string | null; bestOfByRound?: number[] } | null;

  return (
    <div style={{ maxWidth: '720px', margin: '0 auto', padding: 'var(--page-pad)' }}>

      {showTypePicker && (
        <TournamentTypePicker
          onSelectTimed={() => { setShowTypePicker(false); setShowNew('timed'); }}
          onSelectStandard={() => { setShowTypePicker(false); setShowNew('standard'); }}
          onCancel={() => setShowTypePicker(false)}
          timedPricePence={timedPricePence}
          standardPricePence={standardPricePence}
          paymentsEnabled={paymentsEnabled}
        />
      )}
      {showNew && !showBestOfNew && (
        <TournamentDialog
          mode="new"
          initialStyle={showNew === 'standard' ? 'standard' : 'timed'}
          {...(pendingNewDialogData ? dialogDataToInitialProps(pendingNewDialogData) : { initialName: 'Shootout Comps' })}
          saving={dialogSaving}
          onSave={handleNewDialogSubmit}
          onCancel={() => { setShowNew(false); setPendingNewDialogData(null); }}
        />
      )}
      {showNew && showBestOfNew && pendingNewDialogData && (
        <BestOfByRoundDialog
          maxPlayers={pendingNewDialogData.maxPlayers}
          initialBestOfByRound={pendingNewDialogData.bestOfByRound}
          saving={dialogSaving}
          onSave={handleBestOfNewSave}
          onBack={() => setShowBestOfNew(false)}
        />
      )}
      {showPaymentGate && pendingPaymentData && (
        <PaymentGate
          onPay={handlePaymentSuccess}
          onCancel={() => { sessionStorage.removeItem('pendingTournament'); setShowPaymentGate(false); setPendingPaymentData(null); setPaymentError(''); }}
          processing={paymentProcessing}
          error={paymentError}
          amountPence={paymentAmountPence}
          format={pendingPaymentData?.format}
        />
      )}
      {showEdit && editingTournament && editingTournament.status === 'registration' && !showBestOfEdit && (
        <TournamentDialog
          mode="edit"
          {...(pendingEditDialogData ? dialogDataToInitialProps(pendingEditDialogData) : {
            initialName: editingTournament.name,
            initialMaxPlayers: editAny?.maxPlayers,
            initialScheduledAt: editAny?.scheduledAt,
            initialIsHandicap: editAny?.isHandicap ?? false,
            initialLocation: editAny?.location,
            initialEntryFee: editAny?.entryFee,
            initialCurrency: editAny?.currency ?? 'GBP',
            initialPrizes: editAny?.prizes ?? [],
            initialHighBreakPrize: (editingTournament as unknown as { highBreakPrize?: number | null }).highBreakPrize,
            initialFormat: editAny?.format ?? 'shootout',
            initialSixReds: editAny?.sixReds ?? false,
            initialIsPublic: editAny?.isPublic ?? false,
          })}
          saving={dialogSaving}
          onSave={handleEditDialogSubmit}
          onCancel={() => { setShowEdit(false); setEditingTournament(null); setPendingEditDialogData(null); }}
        />
      )}
      {showEdit && showBestOfEdit && pendingEditDialogData && (
        <BestOfByRoundDialog
          maxPlayers={pendingEditDialogData.maxPlayers}
          initialBestOfByRound={pendingEditDialogData.bestOfByRound ?? editAny?.bestOfByRound}
          saving={dialogSaving}
          onSave={handleBestOfEditSave}
          onBack={() => setShowBestOfEdit(false)}
        />
      )}
      {posterTournament && (
        <PosterModal
          tournament={posterTournament}
          onClose={() => setPosterTournament(null)}
        />
      )}
      {cancelTarget && (
        <ConfirmModal
          title="Cancel Tournament"
          message={`Delete "${cancelTarget.name}"? This can't be undone — it's only possible while still in registration.`}
          confirmLabel={cancelling ? '…' : 'Delete'}
          cancelLabel={t.cancelLabel}
          danger
          onConfirm={handleCancelConfirmed}
          onCancel={() => { setCancelTarget(null); setCancelError(''); }}
        />
      )}
      {cancelError && (
        <div style={{ background: 'rgba(192,57,43,0.15)', border: '1px solid var(--red)', borderRadius: '8px', padding: '0.75rem 1rem', color: '#e74c3c', fontSize: '0.85rem', marginBottom: '1rem' }}>
          {cancelError}
        </div>
      )}
      {adminsTarget && (
        <ManageAdminsDialog
          tournamentId={adminsTarget._id}
          tournamentName={adminsTarget.name}
          onClose={() => setAdminsTarget(null)}
        />
      )}
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.75rem', gap: '1rem', flexWrap: 'wrap' }}>
        <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(1.8rem,6vw,2.5rem)', color: 'var(--gold)', lineHeight: 1 }}>{t.tournamentsHeading}</h2>
        <button onClick={() => standardEnabled ? setShowTypePicker(true) : setShowNew('timed')} style={{ background: 'linear-gradient(135deg, var(--gold), var(--gold-light))', color: 'var(--baize-dark)', border: 'none', borderRadius: '8px', padding: '0.65rem 1.25rem', fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer', minHeight: '44px', whiteSpace: 'nowrap' }}>
          + New Tournament
        </button>
      </div>

      {/* My Tournaments — every tournament the user owns, any status. A user may run several
          concurrently, so there's no single "current" one singled out anymore. */}
      <div style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.75rem', fontWeight: 600 }}>
        {t.tournamentsHeading} ({tournaments.length})
      </div>
      {tournaments.length === 0 && (
        <div style={{ textAlign: 'center', color: 'var(--chalk-dim)', padding: '2.5rem', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', border: '2px dashed rgba(255,255,255,0.1)' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📋</div>
          <p style={{ fontSize: '0.88rem' }}>{t.noHistoryYet}</p>
          <p style={{ fontSize: '0.78rem', marginTop: '0.3rem', opacity: 0.7 }}>{t.completedTournamentsAppearHere}</p>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
        {tournaments.map((tt, i) => {
          const statusInfo = STATUS_LABEL[tt.status] ?? { label: tt.status, colour: 'var(--chalk-dim)' };
          const realPlayers = tt.players.filter(p => !p.isBye);
          const isPaused = pausedIds.has(tt._id);
          const isOwned = tt.ownerId === user?._id;
          return (
            <div key={tt._id}
              onClick={() => tt.status === 'complete' ? onViewHistoric(tt._id) : onOpenTournament(tt._id, tt.status)}
              style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '1rem 1.15rem', cursor: 'pointer', transition: 'all 0.15s', animation: `fadeIn 0.3s ease ${i * 0.04}s both` }}
              className="history-row"
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '1.1rem' }}>
                <div style={{ fontFamily: 'Bebas Neue', fontSize: '1.5rem', color: 'rgba(201,168,76,0.35)', minWidth: '24px', textAlign: 'center', lineHeight: 1 }}>
                  {tournaments.length - i}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tt.name}</div>
                  <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.25rem', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)' }}>📅 {formatDate(tt.scheduledAt || tt.createdAt)}</span>
                    <span style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)' }}>👥 {realPlayers.length}{tt.maxPlayers ? `/${tt.maxPlayers}` : ''}</span>
                    <button
                      onClick={e => { e.stopPropagation(); handleTogglePublic(tt._id, !!tt.isPublic); }}
                      disabled={togglingPublicId === tt._id}
                      title={t.togglePublicHint}
                      className="toggle-public-btn"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                        background: tt.isPublic ? 'rgba(52,152,219,0.14)' : 'rgba(255,255,255,0.07)',
                        color: tt.isPublic ? '#5dade2' : 'var(--chalk-dim)',
                        border: `1px solid ${tt.isPublic ? 'rgba(52,152,219,0.4)' : 'rgba(255,255,255,0.18)'}`,
                        borderRadius: '20px', padding: '0.2rem 0.6rem 0.2rem 0.5rem',
                        fontSize: '0.72rem', fontWeight: 600,
                        cursor: togglingPublicId === tt._id ? 'default' : 'pointer',
                        transition: 'all 0.15s', opacity: togglingPublicId === tt._id ? 0.6 : 1,
                      }}
                    >
                      {togglingPublicId === tt._id ? '…' : tt.isPublic ? `🌐 ${t.publicBadge}` : `🔒 ${t.privateBadge}`}
                      <span style={{ fontSize: '0.62rem', opacity: 0.65 }}>⇄</span>
                    </button>
                  </div>
                </div>
                {!isOwned && (
                  <span style={{ fontSize: '0.62rem', fontWeight: 700, background: 'rgba(52,152,219,0.12)', color: '#5dade2', border: '1px solid rgba(52,152,219,0.3)', borderRadius: '5px', padding: '0.15rem 0.45rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    🤝 {t.sharedBadge}
                  </span>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.2rem', flexShrink: 0 }}>
                  <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: statusInfo.colour }} />
                  <span style={{ fontSize: '0.6rem', color: statusInfo.colour, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{statusInfo.label}</span>
                </div>
                <span style={{ color: 'var(--chalk-dim)', fontSize: '1rem' }}>›</span>
              </div>

              {/* Row actions — only for non-complete tournaments, stopPropagation so they
                  don't also trigger the row's own open/view click. */}
              {tt.status !== 'complete' && (
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                  {tt.status === 'registration' && (
                    <button onClick={() => openEdit(tt._id)} style={{ background: 'rgba(201,168,76,0.12)', color: 'var(--gold)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '6px', padding: '0.35rem 0.75rem', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', minHeight: '32px' }}>
                      {t.editBtn}
                    </button>
                  )}
                  <button onClick={() => openPoster(tt._id)} style={{ background: 'rgba(52,152,219,0.12)', color: '#5dade2', border: '1px solid rgba(52,152,219,0.3)', borderRadius: '6px', padding: '0.35rem 0.75rem', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', minHeight: '32px', whiteSpace: 'nowrap' }}>
                    {t.posterBtn}
                  </button>
                  {tt.status === 'active' && tt.format !== 'standard' && (
                    <button onClick={() => handlePauseRow(tt._id)} disabled={pausingId === tt._id} style={{ background: isPaused ? 'rgba(46,204,113,0.12)' : 'rgba(230,126,34,0.12)', color: isPaused ? '#2ecc71' : '#e67e22', border: `1px solid ${isPaused ? 'rgba(46,204,113,0.35)' : 'rgba(230,126,34,0.3)'}`, borderRadius: '6px', padding: '0.35rem 0.75rem', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', minHeight: '32px' }}>
                      {pausingId === tt._id ? '…' : isPaused ? t.resumeClocks : t.pauseClocks}
                    </button>
                  )}
                  {isOwned && (
                    <button onClick={() => setAdminsTarget(tt)} style={{ background: 'rgba(155,89,182,0.12)', color: '#af7ac5', border: '1px solid rgba(155,89,182,0.3)', borderRadius: '6px', padding: '0.35rem 0.75rem', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', minHeight: '32px', whiteSpace: 'nowrap' }}>
                      👥 {t.adminsBtn}
                    </button>
                  )}
                  {isOwned && tt.status === 'registration' && (
                    <button onClick={() => setCancelTarget(tt)} style={{ background: 'rgba(192,57,43,0.12)', color: '#e74c3c', border: '1px solid rgba(192,57,43,0.3)', borderRadius: '6px', padding: '0.35rem 0.75rem', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', minHeight: '32px' }}>
                      Cancel
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <style>{`.history-row:hover{transform:translateY(-1px);box-shadow:0 4px 16px rgba(0,0,0,0.3)} .toggle-public-btn:hover:not(:disabled){filter:brightness(1.25);transform:scale(1.04)} @keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}
